/**
 * Centralized amount formatting utilities.
 *
 * All components should import from here instead of defining their own
 * formatAmount / formatCompactAmount inline.
 */

/**
 * Format a number as a full euro amount: €1,234,567.89
 *
 * @param {number|null|undefined} amount - The amount to format
 * @param {object} [options]
 * @param {string} [options.emptyText=''] - Text to return when amount is 0 / null / undefined
 * @param {string} [options.locale] - Locale override (default: user's locale)
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount, { emptyText = '', locale } = {}) => {
  if (!amount || amount === 0) return emptyText;
  return `€${amount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format a number in compact notation: €1.2M / €500K / €1,234
 *
 * @param {number|null|undefined} amount
 * @returns {string} Formatted compact amount string
 */
export const formatCompactAmount = (amount) => {
  if (amount >= 1_000_000) {
    return `€${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `€${(amount / 1_000).toFixed(0)}K`;
  }
  return `€${amount?.toLocaleString() || 0}`;
};

/**
 * Format an OpenRouter per-token price string into a per-million-token display.
 * OpenRouter returns prices in USD per token (e.g. "0.00000003" = $0.03/M).
 * A value of "-1" means dynamic/variable pricing.
 *
 * @param {string} priceStr - Raw price string from the API
 * @param {function} t - Translation function (for "dynamic" label)
 * @returns {string} Formatted price per million tokens
 */
export const formatPrice = (priceStr, t) => {
  const n = parseFloat(priceStr);
  if (n < 0) return t('aiSettings.dynamicPricing');
  const perMillion = n * 1_000_000;
  if (perMillion >= 10) return perMillion.toFixed(2);
  if (perMillion >= 1) return perMillion.toFixed(3);
  if (perMillion >= 0.01) return perMillion.toFixed(4);
  return perMillion.toFixed(6);
};
